import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type FC,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Chip } from '../Chip/Chip';
import { Icon } from '../Icon/Icon';
import { Link } from '../Link/Link';
import { useClickAway } from '../hooks/useClickAway';
import { copyToClipboard } from '../utils/copyToClipboard';
import { useManagedDomEventListeners } from '../utils/dom/ManagedDomEventListeners';

interface IPAddressListProps {
  addresses: string[];
  removeAddresses: (address: string[]) => void;
  infoMessage?: ReactNode | string;
  disabled?: boolean;
  name?: string;
}

const GAP_BETWEEN_CHIPS = 8; // 0.5rem

export const IPAddressList: FC<IPAddressListProps> = ({
  addresses,
  disabled,
  infoMessage,
  name,
  removeAddresses,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [selectedAddresses, setSelectedAddresses] = useState<string[]>([]);
  const [isCopied, setIsCopied] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const startIndexRef = useRef<number | null>(null);
  const chipListContainerRef = useRef<HTMLDivElement>(null);
  const buttonsContainerRef = useRef<HTMLDivElement>(null);
  const visibleAddresses =
    isExpanded || visibleCount === 0
      ? addresses
      : addresses.slice(0, visibleCount);
  const hiddenCount = addresses.length - visibleCount;
  const chipListRef = useClickAway<HTMLDivElement>((event) => {
    if (!buttonsContainerRef.current?.contains(event.target as Node)) {
      setSelectedAddresses([]);
    }
  }, chipListContainerRef);

  const handleChipClick = (address: string, event: MouseEvent) => {
    if (event.shiftKey) {
      setSelectedAddresses((prevSelected) =>
        prevSelected.includes(address)
          ? prevSelected.filter((item) => item !== address)
          : [...prevSelected, address],
      );
    } else {
      setSelectedAddresses([address]);
    }
  };

  const handleMouseDown = (index: number, event: MouseEvent) => {
    if (event.shiftKey) {
      return;
    }

    const address = addresses[index];
    if (address === undefined) {
      stumble(`No address to select found at index ${index}.`);
      return;
    }

    setIsDragging(true);
    startIndexRef.current = index;
    setSelectedAddresses([address]);
  };

  const handleMouseOver = (index: number) => {
    if (isDragging && startIndexRef.current !== null) {
      const start = Math.min(startIndexRef.current, index);
      const end = Math.max(startIndexRef.current, index);
      const newSelectedAddresses = addresses.slice(start, end + 1);
      setSelectedAddresses(newSelectedAddresses);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    startIndexRef.current = null;
  };

  const calculateVisibleAddressCount = () => {
    if (!chipListRef.current) {
      return;
    }

    const maxRowCount = 3;
    const containerWidth = chipListRef.current.offsetWidth;
    // get all chips excluding the "show all/hide all" button
    const chips = Array.from(chipListRef.current.children).filter(
      (child) => child instanceof HTMLDivElement,
    );
    const chipsWidth = chips.map(
      (chip) => (chip as HTMLDivElement).offsetWidth,
    );

    let totalWidth = 0;
    let rowCount = 0;
    let visibleChips = 0;

    // calculate how many chips can be displayed in a row
    for (const width of chipsWidth) {
      if (totalWidth + width > containerWidth && containerWidth > 0) {
        ++rowCount;
        if (rowCount >= maxRowCount) {
          break;
        }
        totalWidth = 0;
      }
      totalWidth += width + GAP_BETWEEN_CHIPS;
      visibleChips++;
    }

    if (rowCount >= maxRowCount) {
      setVisibleCount(visibleChips);
    }
  };

  useManagedDomEventListeners([window, 'resize', calculateVisibleAddressCount]);
  useManagedDomEventListeners([window, 'mouseup', handleMouseUp]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: recalculate visible address count when addresses change
  useEffect(() => {
    calculateVisibleAddressCount();
  }, [addresses]);

  useEffect(() => {
    if (isCopied) {
      const timeout = setTimeout(() => {
        setIsCopied(false);
      }, 1500);
      return () => {
        clearTimeout(timeout);
      };
    }

    return undefined;
  }, [isCopied]);

  return (
    <div className='flex flex-col gap-xs'>
      {visibleAddresses.length > 0 && (
        <div className='w-fit flex flex-wrap gap-xs py-xs' ref={chipListRef}>
          {visibleAddresses.map((address, index) => (
            <Chip
              dataTestId={`${name ?? ''}-${address}`}
              key={`${index}-${address}`}
              variant='plain'
              size='small'
              onRemove={() => {
                if (disabled) {
                  return;
                }

                removeAddresses([address]);
                setSelectedAddresses([
                  ...selectedAddresses.filter(
                    (selectedAddress) => selectedAddress !== address,
                  ),
                ]);
              }}
              onMouseDown={(event) => handleMouseDown(index, event)}
              onMouseOver={() => handleMouseOver(index)}
              onClick={(event) => handleChipClick(address, event)}
              selected={selectedAddresses.includes(address)}
              className='select-none'
            >
              {address}
            </Chip>
          ))}
          {visibleCount > 0 && addresses.length > visibleCount && (
            <Link
              renderAs='button'
              type='button'
              onClick={() => setIsExpanded(!isExpanded)}
              className='text-blue-500 hover:underline'
              icon={isExpanded ? 'fa-angle-up' : 'fa-angle-down'}
              size='small'
            >
              {isExpanded ? 'Show less' : `Show all (${hiddenCount})`}
            </Link>
          )}
        </div>
      )}
      {infoMessage}
      <div className='flex gap w-fit' ref={buttonsContainerRef}>
        {isCopied ? (
          <div className='text-status-success flex gap-3xs font-semibold items-center text-xs'>
            <Icon size='2xs' iconStyle='solid' glyph='fa-check-circle' />
            Copied to clipboard
          </div>
        ) : (
          <Link
            renderAs='button'
            size='small'
            onClick={async () => {
              await copyToClipboard(
                (selectedAddresses.length === 0
                  ? addresses
                  : selectedAddresses
                ).join(', '),
              );
              setIsCopied(true);
            }}
            icon='fa-copy'
            iconPosition='start'
          >
            Copy {selectedAddresses.length === 0 ? 'all' : 'selected'}
          </Link>
        )}
        <Link
          renderAs='button'
          size='small'
          onClick={() => {
            removeAddresses(selectedAddresses);
            setSelectedAddresses([]);
          }}
          disabled={disabled || selectedAddresses.length === 0}
          icon='fa-trash-can'
          iconPosition='start'
          accent='negative'
        >
          Remove selected
        </Link>
      </div>
    </div>
  );
};
